/**
 * Which side-scroll arrows a scroll box needs (TRA-1166). Pure so the
 * decision is testable without a DOM: the component feeds it the box's
 * live numbers and renders whatever it says.
 */
export interface SideScrollState {
  /** Content is hidden past the left edge — offer a way back. */
  canScrollLeft: boolean
  /** Content is hidden past the right edge — offer a way forward. */
  canScrollRight: boolean
}

/** Browsers report fractional scroll positions; anything under a pixel
    counts as "at the edge" so the arrow does not flicker at the end. */
const EDGE_PX = 1

export function sideScrollState(box: {
  scrollLeft: number
  clientWidth: number
  scrollWidth: number
}): SideScrollState {
  const overflow = box.scrollWidth - box.clientWidth
  if (overflow <= EDGE_PX) {
    return { canScrollLeft: false, canScrollRight: false }
  }
  return {
    canScrollLeft: box.scrollLeft > EDGE_PX,
    canScrollRight: box.scrollLeft < overflow - EDGE_PX,
  }
}

/** How far one click moves: most of the visible width, so the columns at
    the edge stay in view as a landmark. */
export function sideScrollStep(clientWidth: number): number {
  return Math.max(Math.round(clientWidth * 0.8), 1)
}

/** Which arrow: the one that scrolls back, or the one that scrolls on. */
export type SideScrollSide = 'left' | 'right'

/** What happens to the focus ring when the arrows change. */
export type SideScrollFocus =
  | { kind: 'keep' }
  | { kind: 'move'; to: SideScrollSide }
  | { kind: 'clear' }

/**
 * Where keyboard focus goes when the arrows change. An arrow leaves as soon
 * as its side runs out of content, and an element that disappears while
 * focused hands the focus ring back to the document. Without a hand-off,
 * tabbing to the right arrow and pressing it to the end of the table drops
 * you at the top of the page with no way back but tabbing through it again.
 *
 * `clear` is the case where both arrows go because the table now fits: focus
 * is already back on the document, and the record of which arrow held it has
 * to go too, or the arrow that returns when the table overflows again would
 * take focus from wherever it has since moved.
 */
export function focusHandoff(
  focused: SideScrollSide | null,
  next: SideScrollState,
): SideScrollFocus {
  if (focused === null) return { kind: 'keep' }
  if (focused === 'left' ? next.canScrollLeft : next.canScrollRight) return { kind: 'keep' }
  const other: SideScrollSide = focused === 'left' ? 'right' : 'left'
  const otherIsThere = other === 'left' ? next.canScrollLeft : next.canScrollRight
  return otherIsThere ? { kind: 'move', to: other } : { kind: 'clear' }
}
